/**
 * Pomoć za svaki ekran: čemu služi, šta se na njemu radi, i gde se greši.
 *
 * ZAŠTO JE TEKST ODVOJEN OD EKRANA
 *   Da se čita kao tekst. Uputstvo razbacano po komponentama niko ne pregleda
 *   celo, pa jedan ekran dobije tri rečenice a drugi nijednu. Ovde stoje jedno
 *   uz drugo, pa se vidi koji je mršav.
 *
 * ZAŠTO JE TIP `Record<Ekran, ...>`
 *   Da nov tab ne može da se doda bez pomoći. `View` u operaterskom ekranu je
 *   izveden iz <c>EKRANI_OPERATERA</c>, pa prevodilac traži tekst za svaki —
 *   nije stvar discipline nego tipa.
 *
 * ŠTO OVDE NIJE
 *   Ovo nije opis dugmadi. Ime dugmeta čovek već vidi; ne vidi redosled posla,
 *   šta znači boja, i šta će se pokvariti ako preskoči korak. Zato „Zamke"
 *   nose izmerene slučajeve, a ne opšta upozorenja.
 */

export interface Oznaka {
  /** Boja, ikona ili reč koju čovek vidi na ekranu. */
  znak: string;
  znaci: string;
}

export interface PomocTekst {
  /** Ime ekrana kako stoji na tabu — da se zna da je pomoć za pravi ekran. */
  naslov: string;

  /** Jedna ili dve rečenice: zašto ovaj ekran postoji. */
  cemuSluzi: string;

  /** Posao, u redu u kom se radi. */
  koraci: string[];

  /** Šta znači ono što se vidi. Samo gde ekran stvarno nosi oznake. */
  oznake?: Oznaka[];

  /** Gde se greši. Izmereni slučajevi, ne opšta upozorenja. */
  zamke?: string[];
}

/**
 * Tabovi operaterskog ekrana. Redosled je isti kao na ekranu, da se pomoć i
 * traka tabova ne raziđu.
 */
export const EKRANI_OPERATERA = [
  'planiranje',
  'adrese',
  'teren',
  'rezervacije',
  'pravci',
  'prepreke',
  'isporuceno',
  'analitika',
  'podesavanja'
] as const;

export type EkranOperatera = typeof EKRANI_OPERATERA[number];

/** Svi ekrani aplikacije: operaterovi tabovi plus poslovođin i vozačev. */
export type Ekran = EkranOperatera | 'objekat' | 'vozac';

export const POMOC: Record<Ekran, PomocTekst> = {
  planiranje: {
    naslov: 'Planiranje ture',
    cemuSluzi:
      'Ovde se tura slaže. Interni prenosi i isporuke kupcima idu u isto vozilo, '
      + 'na istom pravcu — zato stoje jedni uz druge, a ne na dva ekrana.',
    koraci: [
      'Pročitaj traku na vrhu ako je crvena. Ako piše da se podaci ne slažu sa '
        + 'ERP-om, prvo to — pogrešna lista je gora od nikakve.',
      'Klikni pravac pod „Pravci za danas" (ostali su pod „Ostali pravci"). '
        + 'Šablon je polazna tačka, ne pravilo: stanice se i dalje dodaju, '
        + 'izbacuju i preređuju.',
      'Levo su interni prenosi — tok ide na turu dugmetom „Na turu"; desno su '
        + 'isporuke kupcima — klik na isporuku je stavlja na turu. Roba za kupca '
        + 'koji dolazi sam u objekat (ili koga vozi prodavnica) stoji levo, kao tok '
        + 'ka tom objektu sa oznakom „za kupca", i na turu ide istim dugmetom.',
      'Na mapi predloga pogledaj šta se nudi usput. Koliko kilometara ponuda '
        + 'dodaje piše u oblačiću i u spisku ispod mape — to je jedini broj po '
        + 'kom se odlučuje, jer blizina sama ne kaže ništa o ceni.',
      'Izaberi dan, vozilo i vozača u „Podešavanja ture". Vozilo i vozača '
        + 'aplikacija NE bira sama — vozaču po potvrdi ture odmah ode poruka, pa '
        + 'izbor mora biti tvoj; dok nisu izabrani, podešavanja stoje otvorena, '
        + 'a izabrano piše u naslovu i kad su sklopljena. Kad slažeš dan ranije, '
        + 'izaberi „Sutra": uz svakog vozača piše sutrašnja smena, ko sutra ne '
        + 'radi stoji sivo („ne radi") i ne može da se izabere, a već izabran '
        + 'vozač koji sutra ne radi se skida (ne zamenjuje se sam). Popunjenost '
        + 'i procena vremena računaju se na zbir svih stanica, ne po jednom mestu.',
      'Proveri da li tura upada u smenu DOK je slažeš. Posle potvrde je '
        + 'prepravljanje posao, ne klik. Ako vozač istog dana već ima turu, njeno '
        + 'vreme je oduzeto od smene i to piše sivom rečenicom odmah ispod brojki '
        + 'Popunjenost i Trajanje.',
      '„Sačuvaj turu". Ako je popunjenost pod pragom, dugme ne radi dok ne '
        + 'upišeš razlog.'
    ],
    oznake: [
      { znak: 'crvena čioda', znaci: 'stanica koja ide u ovu turu' },
      {
        znak: 'plava čioda',
        znaci: 'nudi se usput; koliko kilometara dodaje piše u oblačiću i u spisku. '
          + 'Taj broj je PROCENA (vazdušna linija × faktor puta, zaokružena, sa '
          + '„≈"), a kilometri same ture su izmereni po putu kad rutiranje radi — '
          + 'zato se ta dva broja ne slažu do u kilometar.'
      },
      {
        znak: 'crvena traka na vrhu',
        znaci: 'nešto sa ERP-om traži pažnju — pročitaj rečenicu: kad se saldo ne '
          + 'poklapa, ne planiraj; kad piše da je ERP otvorio novu godinu, planiraj '
          + 'normalno i javi da se veza prebaci; kad piše „ERP trenutno ne odgovara", '
          + 'prijava važi, ali spiskovi iz ERP-a mogu biti prazni dok se veza ne vrati '
          + '(traka nestaje kad osvežiš stranicu, najkasnije minut pošto se ERP vrati); '
          + 'kad piše da baza aplikacije nije ažurirana — to nije pad ERP-a, nego posle '
          + 'nove verzije nije pokrenuta migracija: javi da se pokrene; '
          + 'kad piše da isporuka nije upisana u ERP — nije greška planiranja, javi da '
          + 'se otpremnica proknjiži rukom; kad piše „Mapa nije učitana", tura se slaže '
          + 'i bez mape, ali bez predloga usput'
      },
      {
        znak: 'narandžasta ivica toka i „N od M stavki preko X d"',
        znaci: 'roba za objekat čeka duže od dozvoljenog (Podešavanja → Brzina ili '
          + 'popunjenost); takav tok ide na vrh i treba da krene i kad popunjenost '
          + 'nije idealna'
      },
      { znak: 'crveni broj dana uz isporuku', znaci: 'kupac čeka duže od dozvoljenog za kupce' },
      {
        znak: '„Ostavljeno po strani" / „verovatno neproknjižen prijem"',
        znaci: 'interni nalog stariji od mesec dana; ne ulazi u predloge, ali sme na turu'
      },
      {
        znak: '„N bez potvrđenog odredišta" / „izvor nepoznat" (narandžasto)',
        znaci: 'ne zna se sigurno kuda, odnosno odakle roba ide — potvrdi odredište ispod liste'
      },
      { znak: '„ne vozimo mi"', znaci: 'razvoz vozi treće lice (kurir); ne ide na turu' },
      {
        znak: '„za kupca" uz tok',
        znaci: 'roba za kupca koji dolazi sam u objekat (ili koga vozi prodavnica), a stoji '
          + 'drugde; ide pravo u objekat — izbora „u magacin, druga noga" nema. Desno, pod '
          + '„Roba za kupce u objektu", piše samo gde je'
      },
      {
        znak: '„Magacin ne pokriva sve"',
        znaci: 'više naloga traži isti artikal nego što ga ima — proveri sa magacinom pre utovara'
      },
      {
        znak: '„ide bez:" / „već isporučeno:" / „proveri napomenu:"',
        znaci: 'kupac pristao da deo stigne kasnije; to smo već predali (ne planiraj ponovo); '
          + 'napomena liči na „kupac nosi sam", ali nije važeća oznaka — pitaj prodavca'
      },
      {
        znak: '„roba: X ili Y" / „roba razbacana:"',
        znaci: 'odakle se roba diže; „razbacana" znači da nijedno mesto nema sve'
      },
      {
        znak: 'Popunjenost zeleno / narandžasto / crveno',
        znaci: 'u redu / ispod praga / preko zapremine (i usput, posle preuzimanja)'
      },
      { znak: 'Trajanje narandžasto, „preko smene"', znaci: 'tura ne staje u preostalu smenu vozača' },
      {
        znak: '„—" i „računa se" umesto brojki',
        znaci: 'vozilo ili vozač još nisu izabrani — ništa se ne računa dok ih ne izabereš'
      },
      {
        znak: 'crvena / narandžasta / siva rečenica ispod procene',
        znaci: 'crvena blokira čuvanje (vozač ne radi, adresa bez tačke, isporuka ili '
          + 'interni nalog već na drugoj turi — nalog se sme podeliti po količini); '
          + 'narandžasta je upozorenje; siva je obaveštenje'
      },
      { znak: '„(privremeno)" uz zapreminu vozila', znaci: 'zapremina nije izmerena, pa je procenat grub' },
      {
        znak: 'narandžast / zelen broj stanice',
        znaci: 'kućna adresa kupca / preuzimanje robe usput'
      },
      {
        znak: 'crveni red u „Tovar po stanicama"',
        znaci: 'posle te stanice vozilo je prepunjeno; podebljan red je najpunije mesto'
      },
      { znak: '📏 ispred osnove procene', znaci: 'procena iz izmerenih vremena, ne iz podrazumevanih' },
      { znak: 'zelena čioda ⌂ / narandžasta čioda P', znaci: 'polazište i povratak / preuzimanje robe' },
      {
        znak: 'puna crvena / plava isprekidana linija',
        znaci: 'stvarni put ture / linija pravca'
      },
      { znak: 'istaknut pravac u spisku', znaci: 'pravac koji po danima važi danas' },
      {
        znak: '„proveri izvor" uz isporuku',
        znaci: 'poslovođina odluka pokazuje na magacin gde robe više nema (lager '
          + '0), a nije je odnela ni prodaja ni naš prenos u 012. Ne blokira: ako '
          + 'je poslovođa robu video na polici, tura može; inače neka prvo potvrdi '
          + 'ponovo, da vozilo ne ode po ništa.'
      }
    ],
    zamke: [
      'Upozorenja ne blokiraju. Smeš svesno da nastaviš, ali razlog za nisku '
        + 'popunjenost se upisuje — po tome se posle vidi da li se kompromis '
        + 'brzina–popunjenost drži ili se pravda.',
      'Isporuka sa nepotvrđenom adresom se vidi u listi, sa razlogom, ali u '
        + 'turu ne može. Nju rešavaš na „Adrese za potvrdu". Da je sakrijemo, '
        + 'mislio bi da nemaš posla.',
      'Predlog pravca se ne sastavlja dok polazište nema potvrđenu tačku na '
        + 'mapi — umesto mape piše crveno šta fali. Do 15.10.2026. je predlog '
        + 'tada računao od tačke (0,0) i nudio robu po pogrešnim rastojanjima.',
      '„Kraj ture" je podrazumevano isti kao polazište. Promeni ga kad se vozilo '
        + 'vraća drugde — npr. prenoćilo je u Kuli, a vraća se u centralni magacin: '
        + 'tu se računa povratak, tu ostaje vozilo i tu se smešta pretovar. Do '
        + '16.10.2026. kraj je uvek bio polazište, pa je takva tura bila Kula–Kula.',
      'Posle sačuvane ture vozilo i vozač se biraju iznova, a polazište opet sme '
        + 'da predloži gde je vozilo ostalo. Dan („Sutra") i polazište ostaju kako '
        + 'su bili — proveri ih pre sledeće ture.',
      'Nalog se ne deli sam. Ako roba ne staje u vozilo, upozorenje kaže KOJI '
        + 'nalog i koliko: kad jedan nalog sam ne staje, razbij ga na dva u ERP-u '
        + 'ili uzmi veće vozilo; kad bi svaki stao a zbir ne, skini neki nalog ili '
        + 'stanicu. Do 15.10.2026. je pisalo samo „roba prelazi zapreminu", bez '
        + 'naloga.',
      'Isporuka kupcu koja je već na otvorenoj turi ne može da se sačuva u '
        + 'drugu — prvo je skini sa one ture. Do 14.10.2026. je to prolazilo '
        + '(dva operatera ili star prozor), i kupcu su mogla da krenu dva vozila.',
      'Ako je neko mesto prazno a očekuješ robu: prvo pogledaj u predlogu „Roba '
        + 'nije na ovom pravcu" i „Roba razbacana", pa „Ostavljeno po strani" kod '
        + 'internih; tek ostalo je na „Ne može u turu".',
      'Uz isporuku piše „proveri izvor: Roba je možda u vozilu…" kad je njena '
        + 'stanica skinuta sa ture posle polaska — isporuka se vraća u planiranje, '
        + 'a roba je verovatno u kombiju. Pitaj vozača pre utovara (od 16.10.2026).',
      'Na kupčevu stanicu ide samo roba koja stvarno putuje. Ono što piše uz '
        + '„ide bez:" i „već isporučeno:", i ono što kupac nosi sam, ne ide na turu '
        + 'ni u popunjenost. Do 16.10.2026. je tura to nosila, pa je „Predao sam '
        + 'sve" odloženi aparat vodio kao isporučen. Isto važi za isti artikal u '
        + 'dva reda: predat komad zatvara samo jedan red, a drugi putuje (do '
        + '16.10.2026. su se zatvarala oba).',
      'Kilometri ponude usput računaju se na prvobitni predlog i ne menjaju se '
        + 'dok dodaješ stanice — posle nekoliko dodatih su grublji.',
      'Procena zadržavanja zna koliko je roba krupna, ne samo koliko je '
        + 'komada. Tri stotine kablova više ne traže koliko i tri stotine '
        + 'veš-mašina — ali roba bez upisane zapremine se i dalje broji kao '
        + 'pun komad, pa takva stanica ume da ispadne duža nego što jeste.',
      'Kad izabereš vozilo, polazište ume samo da skoči tamo gde je vozilo '
        + 'ostalo — i tada se ceo pravac preslaže sa tog kraja. Uz to stoji '
        + 'rečenica zašto. Čim sam dirneš polazište, aplikacija prestaje da ga '
        + 'pomera do kraja slaganja te ture.',
      'Mesto preuzimanja se računa kad slažeš turu i posle se ne menja samo. '
        + 'Ako se mesto robe kasnije promeni, tura i nalog za pakovanje ostaju na '
        + 'starom — 12.10.2026. je vozač tako poslat u SO2 po robu koja je '
        + 'čekala u magacinu 021. Takva tura na „Sa terena" (i u nalogu za '
        + 'pakovanje) dobija crveni okvir „Preuzimanje se ne slaže sa mestom '
        + 'robe"; okvir ne blokira, pa ga reši pre polaska.',
      'Druga tura istog vozača dobijala je celu smenu, kao da prve nema — do '
        + '14.10.2026. je „staje u smenu" pisalo baš kad je trebalo upozoriti. '
        + 'Sad se vreme prve oduzima. Ako za neku turu piše da joj trajanje nije '
        + 'poznato, ona NIJE oduzeta — računaj sam.',
      'Isto vozilo na turama dva vozača istog dana je upozorenje, ne brana — '
        + 'proveri da se ne preklapaju.',
      'Pravci pod „Pravci za danas" su današnji i kad slažeš turu za sutra.',
      'Kad ERP ne odgovara, tura se i dalje slaže i čuva, ali piše „ERP '
        + 'trenutno nije dostupan" — popunjenost tada nije izračunata, a nije '
        + 'provereno ni da li je roba na polazištu. Do 14.10.2026. takva tura '
        + 'nije mogla ni da se sačuva. Ako možeš da sačekaš, sačekaj.',
      'Tura je do 14.10.2026. bila uvek za danas, a datum se računao po '
        + 'svetskom satu: složena posle ponoći a pre 2h dobijala je jučerašnji '
        + 'datum. Sad se dan bira i računa po našem satu.',
      'Roba za kupca koji dolazi sam (9999001/9999002) ili koga vozi prodavnica '
        + '(9999013), a stoji drugde, od 21.10.2026. ide na turu kao tok „za kupca" '
        + 'ka tom objektu. Do tada je objekat dobijao poruku da sam napravi klasičan '
        + 'prenos kroz 002 — 19.10.2026. je račun 004-1-5500 (klima iz 021 za kupca u '
        + 'Kuli) posle ispravke napomene na 9999002 nestao iz razvoza, a prevoz se nigde '
        + 'nije pojavio. Takav tok sme na turu tek kad je prva noga upisana ili je '
        + 'poslovođa odlučio odakle se roba diže; dotle stoji desno, među zaglavljenima, '
        + 'sa razlogom. Na stanicu drugog objekta ne može — čuvanje ture to odbija.'
    ]
  },

  adrese: {
    naslov: 'Adrese za potvrdu',
    cemuSluzi:
      'Adrese kupaca koje geokoder nije našao dovoljno sigurno. Dok adresa nije '
      + 'potvrđena, roba ne može u rutu — nesigurna koordinata nikad ne ulazi u '
      + 'planiranje.',
    koraci: [
      'Ako na vrhu stoji „Objekti bez potvrđene koordinate", prvo njih — bez '
        + 'tačke magacina ili prodavnice tura sa njima ne može da krene.',
      'Razlog čekanja stoji iznad mape. Pročitaj ga pre nego što gledaš pin.',
      'Pin je već postavljen tamo gde geokoder misli da jeste. Pogledaj nivo '
        + 'pogotka: kod „kućni broj" pin je na nekoj kući, ali adresa je ovde jer '
        + 'nešto ne štima — razlog kaže da li je tuđ broj, drugo selo ili više '
        + 'tačaka; ako je prava kuća, potvrdi. Kod „ulica" i „samo mesto" pin je '
        + 'na ulici ili u centru: pomeri ga na kuću, a nepomeren se potvrđuje samo '
        + 'uz kućicu da je ipak tačno mesto.',
      'Proveri sprat (iz „49/14/04" je već upisan), lift i pristup. „Traži dva '
        + 'čoveka" se samo štiklira od drugog sprata bez lifta — to je predlog, '
        + 'tvoja reč važi.',
      'Posle potvrde sledeća adresa se otvara sama. Ovaj posao se radi '
        + 'desetine puta nedeljno, pa je sve podređeno tome da bude pogled i klik.',
      '„Odbijene adrese" vraćaš u red („Vrati u red") tek kad se sa kupcem '
        + 'dogovori tačna adresa; razlog je obavezan. „Pusti geokoder" obrađuje '
        + 'nove adrese odmah, ne čekajući pozadinu.'
    ],
    oznake: [
      {
        znak: 'kućni broj',
        znaci: 'mapa je našla kuću — ali adresa je u redu jer nešto ne štima (drugi '
          + 'broj, drugo selo, više tačaka); pročitaj razlog'
      },
      { znak: 'ulica', znaci: 'mapa je našla ulicu, ne kuću — pin je negde na ulici' },
      { znak: 'samo mesto', znaci: 'mapa zna samo mesto — pin je u centru, adresu moraš da nađeš' },
      {
        znak: 'ništa',
        znaci: 'nema tačke — mapa nije našla adresu, ili mesto nije razrešeno pa nije '
          + 'ni pitana; postavi pin sam (savetnik se pita sam) ili odbij adresu'
      },
      { znak: 'boja nivoa', znaci: 'zeleno kućni broj, žuto ulica, crveno samo mesto ili ništa' },
      { znak: 'žut razlog iznad mape', znaci: 'pina nema — klikni na mapu da ga postaviš' },
      {
        znak: '„Mesto nije u šifarniku" / „Više mesta nosi ovo ime"',
        znaci: 'mesto nije razrešeno; uz to stoje najbliža mesta sa PTT brojem — '
          + 'pitaj kupca, ili ispravi mesto u ERP-u pa pusti geokoder ponovo'
      },
      {
        znak: '„Pin je postavljen po predlogu …"',
        znaci: 'tačka je za drugačije napisanu ulicu (predlog savetnika) — proveri da je to ta kuća'
      },
      {
        znak: '„PAŽNJA: ne liči na upisano" (žuto)',
        znaci: 'mapa zna tu ulicu, ali možda nije kupčeva — pitaj kupca'
      },
      {
        znak: '„Savetnik misli da je …"',
        znaci: 'verovatan naziv ulice koje nema na mapi — pin postavi ručno'
      },
      {
        znak: 'kućica „Pin nije na kući … nisam ga pomerao"',
        znaci: 'bez nje se nepomeren pin centra mesta ili ulice ne može potvrditi'
      },
      {
        znak: '„Objekti bez potvrđene koordinate"',
        znaci: 'magacin ili prodavnica bez potvrđene tačke — tura sa njima ne može da krene'
      }
    ],
    zamke: [
      'Predlog savetnika koji ne liči na ono što piše na otpremnici stoji poslednji, '
        + 'sa „PAŽNJA: ne liči na upisano". Mapa ga zna, ali to ne znači da je '
        + 'kupčeva ulica — proveri sa kupcem pre nego što prihvatiš pin.',
      'Mesto čije ime nosi više sela u različitim okruzima („Novo Selo"), ili '
        + 'koje bez kvačica glasi kao drugo („Bač"/„Bać"), aplikacija ne pogađa: '
        + 'adresa čeka tebe bez pina, a uz nju stoje sva mesta tog imena sa PTT '
        + 'brojem. Pitaj kupca koje je, pa postavi pin u pravo selo — ili ispravi '
        + 'mesto u ERP-u i pusti geokoder ponovo. Do 15.10.2026. se tiho uzimalo '
        + 'prvo nađeno.',
      'Pin koji je mapa stavila na centar mesta ili na ulicu ne potvrđuje se '
        + 'jednim klikom: pomeri ga na kuću, ili štikliraj da je ipak tačno mesto. '
        + 'Takva adresa posle i dalje nosi nivo „centar mesta/ulica", ne „kućni '
        + 'broj". Do 15.10.2026. je jedan klik pravio od centra mesta „kućni broj, '
        + 'potvrdio operater".',
      'Isto važi za pin koji je postavio savetnik: kad je predlog na nivou ulice '
        + 'ili ne liči na upisanu ulicu, a pin nisi pomerao, traži se štiklirano '
        + '„ipak je ovo tačno mesto", i adresa zadržava nivo predloga. Do 16.10.2026. '
        + 'je jedan klik od takvog predloga pravio „kućni broj".',
      'Sprat iz adrese se ne kuca: kad otpremnica nosi „kuća/stan/sprat" '
        + '(„49/14/04"), sprat je već upisan, i kod adresa koje je mapa potvrdila '
        + 'sama. „Traži dva čoveka" se samo štiklira od drugog sprata bez lifta — '
        + 'to je predlog; kad je dirneš, važi tvoja reč. Proveri sa kupcem lift '
        + 'pre nego što potvrdiš. Do 15.10.2026. se sprat iz adrese bacao.',
      'Sprat i lift saznaješ dok ti je kupac na vezi. Posle je to novi poziv, '
        + 'a vozač u međuvremenu ne zna da li ide sam ili u dvoje.',
      'Tekst adrese se ispravlja u ERP-u, ne ovde. Ovde se postavlja tačka.',
      'Ne potvrđuj pin „otprilike" da bi lista bila kraća. Pogrešna koordinata '
        + 'ne zaustavi turu — pošalje vozača na pogrešno mesto.',
      'Razlog „traži se kućni broj 12, a geokoder je našao 15" znači da pin '
        + 'stoji na TUĐOJ kući. Pomeri ga. Do 14.10.2026. se takva adresa '
        + 'potvrđivala sama.',
      'Razlog „geokoder je adresu našao u mestu Nova Crvenka, a otpremnica '
        + 'traži Crvenka" znači drugo selo, ne grešku u pisanju. Do 14.10.2026. '
        + 'su prolazili Nova Crvenka, Stari Žednik i „Opština Kula"; sad sam '
        + 'prolazi samo Gornji/Donji (npr. Gornji Tavankut za Tavankut).'
    ]
  },

  teren: {
    naslov: 'Sa terena',
    cemuSluzi:
      'Šta se dešava na putu i šta čeka tvoju odluku. Ekran se osvežava sam, '
      + 'pa može da stoji otvoren dok radiš nešto drugo.',
    koraci: [
      'Izuzeci su na vrhu — to je posao. Napredak tura ispod je informacija.',
      'Za svaki izuzetak izaberi jednu od četiri odluke: vraća se u magacin, '
        + 'ide na novu turu, kupac odustao, ili ipak isporučeno. Stavka se ne '
        + 'zatvara sama — osim odluke „Ipak isporučeno", koja i jeste zapis da je '
        + 'roba predata.',
      'Kad vozač prijavi da kupca nema, pozovi kupca pa odluči. Vozač ne '
        + 'odlučuje o ponovnoj isporuci.',
      'Upis u ERP posle isporuke (prenos 012 → objekat i kukičanje otpremnice) '
        + 'ide tek kad se tura zatvori — do tada vozač sme da vrati pogrešan štrik. '
        + 'Tura koju vozač zaboravi da zatvori drži i te upise, pa je zatvori ti.',
      'Ako je posle zatvaranja ture nešto pogrešno štiklirano kao predato, '
        + 'otvori turu i uz stavku klikni „nije predato", sa razlogom. Ako je za '
        + 'tu stavku već upisano u ERP (prenos 012 → objekat ili kukičanje), '
        + 'aplikacija ništa ne menja i kaže šta prvo treba ispraviti u ERP-u. Kad '
        + 'otpremnicu u ERP-u vratiš na neisporučeno, ponovi „nije predato" — '
        + 'aplikacija to proveri u ERP-u i pusti (do 16.10.2026. je odbijala zauvek). '
        + 'E-faktura se otvara odmah, jer se kukiči samo kod nas.',
      'Ako tura nije završena, roba ostaje u vozilu preko noći. Ujutru odluči: '
        + 'ostaje u istom vozilu, vraća se u magacin, ili čeka drugo vozilo. '
        + 'Odluka se pamti kao mesto robe: nalog za pakovanje sledeće ture uz tu '
        + 'stavku piše da je u vozilu (ili da je treba pretovariti iz drugog), a '
        + 'ne šalje magacionera na policu. Kad turu zatvoriš ti umesto vozača, '
        + 'pokupljena roba pretovara takođe stoji pod „Ostalo u vozilima" dok ne '
        + 'kažeš gde je.',
      'Odeljak „Online bez odluke" su otpremnice online prodaje kojima '
        + 'napomena ne kazuje ko vozi. Pozovi online i zamoli da isprave napomenu '
        + 'u ERP-u — oznaka je „elbs" kad vozimo mi (uz mesto, npr. „elbs '
        + 'mpo004" ili „elbs mag021", roba se diže tamo), „city" ili „gebruder" '
        + 'za kurira, a „9999001"/„9999002" uz mesto (mp002, mag021; prolazi i '
        + '„mpo 002") kad kupac preuzima sam. Dok to stoji ovako, isporuka ne '
        + 'ulazi u planiranje. Ako napomena kaže jedno mesto, a stavka računa '
        + 'drugo, otpremnica takođe stoji ovde — treba ih uskladiti u ERP-u.',
      'Odeljak „Upis u ERP nije prošao" rešavaj po savetu koji stoji uz red. '
        + 'Ime i vreme uz red su PRVI pokušaj (otkad problem stoji), „poslednji '
        + 'pokušaj" je zadnje ponavljanje, a „Prvi pad" u belešci čuva prvi '
        + 'razlog — „Servis kaže" je razlog zadnjeg. '
        + 'Kad piše da roba nije odvojena, javi poslovođi da ponovi potvrdu '
        + 'mesta otpreme. Drugu nogu (012 → objekat) aplikacija ponavlja sama na '
        + 'pet minuta; ako i dalje ne prolazi, uradi prenos rukom u ERP klijentu '
        + 'i ODMAH klikni „Urađeno rukom" uz red. Kad savet kaže da je upis '
        + 'nedovršen (aplikacija prekinuta usred upisa), ne radi ništa rukom: '
        + 'aplikacija ga u pozadini traži u ERP-u i sama dopiše da li je prošao '
        + 'ili nije. Isto važi za red „povrat rezervacije" (012 → izvor, kupac '
        + 'odustao): aplikacija ga ponavlja sama, a ako ga uradiš rukom, klikni '
        + '„Urađeno rukom" — aplikacija proverava da je komad izašao iz 012. '
        + 'Red „zatvaranje rezervacije" čiji izvor NIJE 012 znači da je '
        + 'roba isporučena posle povrata u prodaju (kupac odustao pa se '
        + 'predomislio): taj prenos izvor → objekat aplikacija ne radi sama — '
        + 'uradi ga rukom u ERP-u i klikni „Urađeno rukom". Red nestaje sam kad '
        + 'ponovni pokušaj prođe, kad se nedovršen upis nađe u ERP-u, ili kad '
        + 'posao izgubi smisao; ostalo čeka tebe.'
    ],
    oznake: [
      {
        znak: '„vozilo stalo u HH:MM"',
        znaci: 'vozač je javio kvar; tura nije zatvorena — odluči šta dalje sa robom'
      },
      {
        znak: 'crvena traka „Poruke ne idu — kanal"',
        znaci: 'poruke tim kanalom stoje u redu; kupac ne zna da mu roba stiže, a '
          + 'vozač možda nije dobio link — proveri nalog u Podešavanja → Slanje poruka'
      },
      {
        znak: 'crvena traka „Pozadinski posao ne radi"',
        znaci: 'posao koji aplikacija radi sama (uvoz adresa, slanje poruka, zatvaranje '
          + 'rezervacija, kukičanje, GPS, javljanje objektima, podsetnik za prijem) pao je '
          + 'tri puta zaredom; traka kaže šta zbog toga ne stiže. Posao pokušava sam — ako '
          + 'traje, javi da se proveri server. Do 16.10.2026. to se videlo samo u dnevniku '
          + 'servera'
      },
      { znak: '„N izuzetak" uz turu', znaci: 'toliko izuzetaka na turi čeka tvoju odluku' },
      {
        znak: '„vozilo na putu N min, tura nije otvorena"',
        znaci: 'po GPS-u je vozilo krenulo, a vozač turu na telefonu nije otvorio — '
          + 'ništa se ne meri; pozovi ga'
      },
      {
        znak: '„nije krenula" i „odbaci"',
        znaci: 'sa terena nema nijednog traga za tu turu; odbaci je samo dok stoji. '
          + 'Kad vozač već radi na stanicama (i bez „Krenuo sam"), umesto „odbaci" '
          + 'stoji „zatvori". Ako prozor za odbacivanje kaže da je vozilo po GPS-u '
          + 'izašlo, prvo pozovi vozača — možda vozi bez signala'
      },
      {
        znak: 'odeljak „Stoji u magacinu 012" (crveni okvir, broj)',
        znaci: 'roba je izašla iz objekta, a nije stigla nikuda; broj su stavke koje '
          + 'tu stoje predugo'
      },
      {
        znak: 'odeljak „Ostalo u vozilima"',
        znaci: 'roba sa završenih tura koja nije predata — ujutru odluči gde ide. '
          + 'Tu je i roba sa stanice skinute POSLE polaska (do 16.10.2026. je '
          + 'nestajala iz evidencije); stanica skinuta pre polaska nije utovarena'
      },
      {
        znak: 'odeljak „U ERP-u zatvoreno, a stoji na turi"',
        znaci: 'otpremnica je u ERP-u isporučena, a naš vozač nije predao ništa — '
          + 'roba je otišla drugim putem; skini je sa ture'
      },
      {
        znak: 'odeljak „Vozač je bio na skinutoj stanici"',
        znaci: 'stanica je skinuta, a roba na njoj predata — ta isporuka ne stoji '
          + 'nigde dok je ne razrešiš'
      },
      {
        znak: '„nema javljanja"',
        znaci: 'tura je u toku, a sa telefona ništa ne stiže duže od 90 minuta — '
          + 'najčešće je vozač bez signala; ako traje, pozovi ga'
      },
      {
        znak: '„nije prošlo" uz poruku',
        znaci:
          'Poruka nije stigla. Crveno je dok je niko ne skloni: kad pozoveš '
          + 'primaoca, klikni „pozvao sam ga" — red ostaje, sivo, sa „rešeno" i '
          + 'tvojim imenom, i više se ne broji. Uz red piše kome je išla (Kupcu, '
          + 'Vozaču, Objektu) i kojim kanalom (Viber, SMS, mejl). Kad isti '
          + 'poslovođa vodi dva objekta, podsetnik za prijem stiže jednom, za oba. '
          + 'Sklonjena poruka stoji sivo do kraja dana, pa nestaje.'
      },
      {
        znak: 'roba nije odvojena',
        znaci:
          'Rezervacija nije upisana u ERP, pa komad nije u magacinu 012 — drugi '
          + 'objekat ga i dalje može prodati. To je jedini slučaj na ovom ekranu '
          + 'od kog se komad može stvarno izgubiti, pa taj odeljak stoji odmah '
          + 'ispod „Čeka odluku", sa crvenom ivicom.'
      },
      {
        znak: 'nema ga u ERP-u',
        znaci:
          'Nalog je upisan kod nas, a u ERP-u ga nema — neko ga je tamo '
          + 'poništio. Dugme „Poništen je, oslobodi robu" zatvara naš zapis. '
          + 'Šta to znači zavisi od noge: poništena rezervacija (prva noga) '
          + 'oslobađa robu i stavka se može ponovo rezervisati; poništena druga '
          + 'noga ili povrat ponovo otvaraju rezervaciju — roba je i dalje u 012 '
          + 'i aplikacija prenos ponavlja sama, a ako je urađen rukom, vidi to po '
          + 'stanju 012 i traži „Urađeno rukom". Poruka posle klika kaže koje je '
          + 'od toga bilo. Pazi: dugme NE briše dokument u ERP-u, nego samo '
          + 'priznaje da ga nema.'
      },
      {
        znak: 'nalog X / čeka N h / možda u 002 (odeljak „Prenos u magacin 012")',
        znaci:
          'Prva noga posle poslovođine odluke. „nalog X": izabrani objekat je '
          + 'napravio prenos u 012 — nalog se priznaje samo tom objektu i pokriva '
          + 'onoliko otpremnica koliko ima komada, a nalog iz drugog objekta se ne '
          + 'računa. „čeka N h": naloga nema; preko 18 h je crveno, jer se tura '
          + 'tad već slaže. „možda u 002": isti objekat je taj artikal poslao u '
          + 'stari magacin prenosa — može biti promašaj, može i redovna dopuna, '
          + 'proveri pre nego što zoveš.'
      },
      {
        znak: 'nema adrese',
        znaci:
          'Online je napisala da robu vozimo mi, a mesto kupca nije upisano. Bez '
          + 'mesta se adresa ne može naći na mapi, a nesigurna koordinata nikad '
          + 'ne ulazi u rutu — pa isporuka stoji dok se mesto ne dopiše u ERP-u. '
          + 'To je jedini red na tom spisku koji stvarno zaustavlja posao.'
      },
      {
        znak: 'nalog X…',
        znaci:
          'Prenos magacin–magacin. Takvi se NE knjiže i zato ne stoje na '
          + '„Nalozi za knjiženje“ — pojave se tamo samo ako ih u ERP-u nema. '
          + 'Nalog kome je prva cifra objekat (40011, 80020) je maloprodajni i '
          + 'njega knjiženje čeka.'
      },
      {
        znak: 'crveni okvir „Preuzimanje se ne slaže sa mestom robe"',
        znaci:
          'U otvorenoj turi: stanica preuzimanja stoji na jednom mestu, a roba '
          + 'je po našem dnevniku na drugom — obično zato što je mesto promenjeno '
          + 'posle slaganja ture. Ako tura još nije krenula, odbaci je i složi '
          + 'ponovo — predlog doda preuzimanje tamo gde roba stoji; ako je krenula, '
          + 'javi vozaču gde roba stoji. Preuzimanje se na sačuvanu turu ne dodaje '
          + 'kroz aplikaciju (do 16.10.2026. je ovde pisalo „dodaj preuzimanje", a '
          + 'dugmeta nije bilo). Isto piše na vrhu naloga za pakovanje.'
      },
      {
        znak: 'žuto „od 12. 10. 2026." uz turu',
        znaci:
          'Tura ranijeg dana koja nije zatvorena. Roba na njoj se i dalje vodi '
          + 'kao rezervisana — zatvori je ili odbaci.'
      },
      {
        znak: 'sivo „za 15. 10. 2026." uz turu',
        znaci:
          'Tura složena dan ranije. Ništa ne fali — njen dan još nije došao. '
          + 'Nalog za pakovanje može već sad; vozaču se tura na telefonu pojavi tek '
          + 'tog dana, da je ne bi otvorio danas.'
      },
      {
        znak: 'sličice uz stanicu u pregledu ture',
        znaci:
          'Fotografije koje je vozač napravio na toj stanici (oštećenje, odbijena '
          + 'roba). Klik otvara punu sliku. Slika napravljena bez signala stigne '
          + 'tek kad se vozač uhvati mreže.'
      }
    ],
    zamke: [
      'Vozačev izuzetak koji nikog ne čeka je zapis koji niko ne čita. Zato '
        + 'izuzeci stoje iznad napretka, a ne pored njega.',
      'Ako pri vrhu „Čeka odluku" piše da izuzeci nisu učitani, osveži — to ne '
        + 'znači da ih nema.',
      'Automatsko kukičanje gleda koliko otpremnica traži u ERP-u: isporuka iz '
        + 'drugog pokušaja se kukiči i kad prvi pokušaj stoji kao nepredat, a stavka '
        + 'dodata u ERP-u posle planiranja drži kukičanje dok ne bude predata. Do '
        + '16.10.2026. se prvo nikad nije desilo, a drugo je kukičalo prerano.',
      'Kad skineš stanicu kupca koji je već dobio poruku „stiže danas" (ili mu ona '
        + 'još čeka u redu), ekran to kaže — pozovi ga. Ako isti kupac ima i drugu '
        + 'stanicu na turi, ekran ćuti: roba mu danas ipak stiže, a vozač ga zove pred '
        + 'dolazak. Najava kupcima ide i kad vozač pritisne „Stigao sam" bez '
        + '„Krenuo sam", a ispravljen broj telefona u ERP-u stiže u aplikaciju i do '
        + 'vozača. Najava se na novi broj NE šalje sama — vozač na toj stanici vidi '
        + '„KUPAC NIJE OBAVEŠTEN" i zove. Do 16.10.2026. ništa od toga nije važilo.',
      'Odluka „kupac odustao" vraća robu u prodaju i sklanja otpremnicu u „Ne '
        + 'vozi se" (na „Ne može u turu"), sa razlogom. Otpremnicu treba stornirati '
        + 'u ERP-u; ako se kupac predomisli, vrati je odande jednim klikom. Do '
        + '16.10.2026. se odmah ponovo nudila za turu.',
      'Izuzetak koji ostaviš otvoren drži robu: ne nudi se za novu turu dok ne '
        + 'odlučiš. Na „Ne može u turu" stoji pod „Čeka tvoju odluku o izuzetku". '
        + 'Tako je od 14.10.2026; do tada se roba odmah vraćala u ponudu, i mogla '
        + 'je da krene kupcu koji ju je odbio.',
      'Crvena traka „ovi delovi ekrana nisu stigli" ne znači da nema posla, '
        + 'nego da se nije moglo pročitati — prazan odeljak tad laže. Ne mora '
        + 'biti ERP: 07.10.2026. su tri dela stajala zbog kvara u našoj '
        + 'aplikaciji, a jedan od njih („stanje reda poruka") ERP ne dira '
        + 'uopšte. Ako se ponavlja posle osvežavanja, uzrok je u dnevniku '
        + 'servera, ne na ekranu.',
      'Spisak „Nalozi za knjiženje“ je do 09.10.2026. gomilao i magacinske '
        + 'prenose (nalog X…), koje niko nikad ne knjiži — izmereno: svih 80 je '
        + 'neproknjiženo, dok je od 122 maloprodajna 119 proknjiženo. Spisak koji '
        + 'se ne može ispraznuti prestaje da se čita, pa ih tamo više nema.',
      'Online prodaja (090) ulazi u logistiku SAMO kad napomena kaže „elbs", i '
        + 'to od 12.10.2026. Obrnuto je od ostalih objekata, gde logistika uzima '
        + 'sve osim izuzetog — i to nije ukus nego mera: izmereno 09.10.2026, od '
        + '233 online otpremnice 101 je kurirska, a 131 bez prepoznatljive oznake. '
        + 'Da je pravilo bilo obrnuto, operater bi dobio svu online prodaju.',
      'Neuspeo upis u ERP se do 09.10.2026. video samo u sekundi klika i nigde '
        + 'posle toga. Zbog toga je greška zbog koje nijedan prenos iz magacina '
        + '002 nikad nije prošao stajala nevidljiva sedam dana. Sad stoji na '
        + 'ovom ekranu — red ovde znači da posao još nije prošao; neki se reše '
        + 'sami ponovnim pokušajem ili kad izgube smisao, ostali čekaju tebe.',
      'Pre poteza pogledaj da li je otpremnica već isporučena. Izmereno '
        + '13.10.2026: sva četiri reda na ovom spisku bila su stara do deset '
        + 'dana, kupci su robu već imali, a u 012 nije stajalo ništa. Ponovljena '
        + 'rezervacija bi tada odvojila robu koje više nema. Takve redove '
        + 'aplikacija sada sklanja sama, u roku od nekoliko minuta.'
    ]
  },

  rezervacije: {
    naslov: 'Rezervacije za kupce',
    cemuSluzi:
      'Roba koju magacin na molbu poslovođe odvoji za kupca pre računa stoji u '
      + 'magacinu 008, na nalogu objekta („ZA KULU REZERVACIJA"). Kad kupac kupi, a '
      + 'trgovac na kasi upiše da roba ide iz magacina Sombor (121), ovde se pojavi '
      + 'alarm: prodata je roba koja je odvojena, i treba je skinuti sa naloga. Tok '
      + 'računa se ne menja — aplikacija samo javlja.',
    koraci: [
      'Kad stigne Viber poruka „PRODATA REZERVISANA ROBA", otvori link iz nje ili '
        + 'ovu karticu; brojka na kartici kaže koliko alarma čeka.',
      'Proveri sa poslovođom da li je to kupac za kog je roba odvojena. Poslovođa '
        + 'dobija istu poruku i može da potvrdi iz svog ekrana — tada na alarmu piše '
        + '„Poslovođa potvrdio: jeste taj kupac".',
      'Ako jeste: skini stavku sa naloga tako da se vrati iz 008 u 021, i kupcu daj '
        + 'baš odvojeni komad. Alarm se zatvori sam kad stavka nestane sa naloga.',
      'Kad na nalogu stoji više istih komada, posle skidanja jednog pritisni '
        + '„Skinuto sa naloga" — aplikacija ne zna koji je komad čiji.',
      'Ako nije taj kupac: „Nije taj kupac" — roba ostaje odvojena za onog za koga '
        + 'je i bila.'
    ],
    oznake: [
      {
        znak: 'alarm „Prodata rezervisana roba"',
        znaci: 'prodata roba stoji na nalogu rezervacije — čeka proveru i skidanje sa naloga'
      },
      {
        znak: '„Stoji na nalogu … N kom"',
        znaci: 'koliko tog artikla stoji na nalogu objekta sada; kad padne, alarm se zatvara sam'
      },
      {
        znak: '„Poslovođa potvrdio: jeste taj kupac"',
        znaci: 'poslovođa je rekao da je to kupac za kog je odvajano — skini sa naloga'
      },
      {
        znak: '„nalog ne kaže objekat"',
        znaci: 'nalog glasi na osobu („ZA NINU REZERVACIJA") — alarm vidi samo magacin, '
          + 'poslovođa ga ne dobija'
      },
      {
        znak: '„Zatvoreno u poslednjih 7 dana"',
        znaci: 'šta je urađeno sa ranijim alarmima i ko je zatvorio'
      }
    ],
    zamke: [
      'Skidanje sa naloga znači VRAĆANJE iz 008 u 021 — ne prebacivanje u 002 ni u '
        + 'objekat. Aplikacija je za račun već uzela jedan komad iz 021; ako se '
        + 'odvojeni prebaci drugde, lager se raspadne baš kao pre ovog alarma.',
      'Ako je odvojeni komad bio jedini u 021, prva noga računa ne prolazi dok se ne '
        + 'skine sa naloga — prvo skini, onda ide sama.',
      'Alarm dolazi samo kad trgovac upiše da roba ide iz magacina Sombor (121). Ako '
        + 'kupac nosi sam ili roba ide iz objekta, alarma nema — tada rezervaciju skini '
        + 'kad ti poslovođa javi.',
      'Opis naloga mora da kaže objekat („ZA KULU REZERVACIJA"), inače alarm ne ide '
        + 'poslovođi. Do 19.10.2026. se odvajalo kroz 002, pa je aplikacija tu robu '
        + 'nudila za prevoz do objekta — zato se sada odvaja u 008, ne u 002 ni u 004 '
        + '(oštećena roba).',
      'Alarm stiže najkasnije desetak minuta posle računa: aplikacija nove račune '
        + 'čita na 10 minuta.'
    ]
  },

  pravci: {
    naslov: 'Pravci',
    cemuSluzi:
      'Mesta kroz koja ustaljeni pravci prolaze, i dani kad se voze. Linija i '
      + 'pojas oko pravca računaju se samo kroz potvrđena mesta, pa mesto koje '
      + 'nije potvrđeno ne dobija predloge „usput".',
    koraci: [
      'Gore, u „Nedeljni raspored", kvačicom zadaj dane vožnje i kojim objektima '
        + 'pravac nosi robu — to je podsetnik, tura ne nastaje sama. Promena '
        + 'objekata ne menja put; put se menja na mapi ispod.',
      'Gledaj LINIJU na mapi, ne pojedinačne tačke. Pogrešna tačka se poznaje '
        + 'po tome što lomi put.',
      'Ako lomi, prevuci tu jednu tačku i potvrdi je dugmetom „Potvrdi pomerenu" '
        + '— tek onda potvrdi celu liniju. Potvrda linije ne nosi pomerene tačke.',
      'Kad put ide kako treba, potvrdi celu liniju jednim potezom.'
    ],
    oznake: [
      {
        znak: 'N/M potvrđeno (zeleno/žuto)',
        znaci: 'zeleno: sva mesta potvrđena; žuto: neka čekaju, pa se kroz njih ne računa pojas'
      },
      { znak: 'zelena / žuta tačka sa brojem', znaci: 'potvrđeno / nepotvrđeno mesto; broj je redosled na putu' },
      { znak: 'bez tačke', znaci: 'mesto nema koordinatu — „Nađi tačku" ili klik na mapu' },
      { znak: '„Potvrdi pomerenu"', znaci: 'tačka je prevučena a nije snimljena — potvrdi je pre linije' }
    ],
    zamke: [
      'Ne klikaj mesta pojedinačno kad linija ide kako treba — ti klikovi ne '
        + 'dodaju nijedan podatak.',
      'Mesto dodato na put ulazi bez tačke i nepotvrđeno: „Nađi tačku", pogledaj '
        + 'gde je palo, potvrdi.',
      'Predlog ture crta liniju i pojas SAMO kroz potvrđena mesta; nepotvrđena '
        + 'preskače i prijavljuje ih. Mesto koje nije potvrđeno zato ne dobija '
        + 'ponude „usput".'
    ]
  },

  prepreke: {
    naslov: 'Ne može u turu',
    cemuSluzi:
      'Roba i isporuke koje ne mogu u turu, i šta treba uraditi da mogu. Svaki '
      + 'od tih podataka je i do sada postojao, ali razbacan po ekranima — pa si '
      + 'gledao praznu listu i nisi imao gde da proveriš zašto je prazna.',
    koraci: [
      'Pročitaj grupu i „šta se radi" uz nju. Spisak bez toga je samo prigovor.',
      'Reši ono što drži robu. Neke odluke se donose ovde, dugmetom uz red '
        + '(roba stigla pre kalkulacije, kupac pristaje bez dela robe, vozi '
        + 'prodavnica sama, ovo se ne vozi); ostalo — adrese, odredište, otvorena '
        + 'tura — ide tamo gde je uzrok. Pa se vrati na planiranje.'
    ],
    oznake: [
      {
        znak: 'danas / N d',
        znaci: 'koliko red čeka; žuto preko 2 dana, crveno kad je roba puštena na reč '
          + 'a kalkulacija ne stiže'
      },
      { znak: 'broj · N kom', znaci: 'koliko redova i komada robe stoji u grupi' },
      { znak: '›', znaci: 'otvara redove grupe; „šta se radi" se vidi i kad je zatvorena' },
      { znak: '„NARUČENO: stiže u X, dd.MM." / „kasni N d"', znaci: 'poslovođino obećanje dolaska robe' },
      { znak: '„ISPORUKA JE NAPLAĆENA"', znaci: 'e-faktura bez objekta, a kupac je platio dostavu — prvo nju' },
      {
        znak: '„Ne vozi se"',
        znaci: 'sklonjeno odlukom operatera; u ERP-u ništa nije promenjeno, vraća se jednim klikom'
      }
    ],
    zamke: [
      'Nisu sve grupe prepreke: „Vozi se bez dela robe" i „Pušteno na reč" idu '
        + 'na turu — ovde stoje da bi se odluka mogla povući.',
      'Odredište iz teksta naloga: mesto odmah posle „IZ" je izvor, ne odredište. '
        + '„IZ APATINA ZA KULU" ide u Kulu, a nalog sa samo „IZ APATINA" čeka tvoju '
        + 'potvrdu odredišta. Do 16.10.2026. je takav tekst išao u mesto posle „IZ".',
      'Delimičan prijem (objekat primi 1 od 2 komada) drži samo svoj nalog: '
        + 'drugi nalog istog artikla za isti objekat ostaje u ponudi, a podsetnik '
        + 'objektu kaže koliko još čeka. Do 16.10.2026. je višak sakrivao tuđi nalog.',
      'Najtiši uzrok je tura od ranijeg dana koja stoji otvorena: roba na njoj '
        + 'je rezervisana i ne nudi se. Takve ture stoje ovde pod „Ture koje stoje '
        + 'otvorene"; ako se neće voziti, odbaci je u kartici „Sa terena". Roba '
        + 'na današnjoj ili budućoj turi se ovde ne vidi.',
      'Prazna lista u planiranju ne znači da nema posla. Znači da ga nešto drži '
        + '— a to nešto je ovde.',
      'Roba za kupca koji dolazi sam u objekat stoji pod „Roba za kupce koja ide u '
        + 'objekat", ne pod adresama: adrese nema, stanica je objekat. Bez svoje grupe '
        + 'bi pala među „Adrese koje čekaju potvrdu" sa uputstvom koje za nju ne važi '
        + '(provereno 19.10.2026). Najčešće čeka prvu nogu ili poslovođinu odluku '
        + 'odakle se diže; roba koja je već u objektu i čeka kupca ovde ne stoji.',
      'Otpremnica na kojoj nema šta da se tovari (sve predato, kupac nosi sam, '
        + 'samo usluga) stoji pod „Otpremnice bez robe za utovar" — tu se izvor ne '
        + 'upisuje. Do 14.10.2026. je stajala pod „Isporuke bez pokrivene robe", '
        + 'sa uputstvom koje za nju nije važilo.',
      '„<artikal>: nema nigde u firmi" znači baš to. Roba koja postoji a ne '
        + 'nudi se ima svoju rečenicu uz red: „obećano za X; na stanju ima u Y, '
        + 'ali se ne nudi dok obećanje stoji", ili „traži 2, a nema mesta sa svim '
        + 'komadima — ima A (1), B (1)". Poslovođina odluka na mesto gde robe ima '
        + 'jača je od obećanja. Do 15.10.2026. je za sve to pisalo „nema nigde", '
        + 'pa se naručivalo ono što već postoji.'
    ]
  },

  isporuceno: {
    naslov: 'Isporučeno',
    cemuSluzi:
      'Šta je isporučeno, po danu PREDAJE, najnoviji dan prvi: tura otvorena '
      + 'preko noći koja je predala jutros stoji pod današnjim danom (do '
      + '15.10.2026. je stajala pod danom kad je počela). Samo isporuka bez '
      + 'ijednog vremena stoji pod danom ture. Pitanje „je li ovo otišlo i kad" '
      + 'do sada je tražilo otvaranje pojedinačne ture.',
    koraci: [
      'Izaberi period gore: 7, 14 ili 30 dana.',
      'Nađi dan, pa stanicu.',
      'Uz vreme pogledaj ODAKLE je došlo. To nije kozmetika — razlikuje '
        + 'predaju robe od prisustva vozila.'
    ],
    oznake: [
      {
        znak: 'vreme bez napomene',
        znaci: 'vozač je štiklirao — čovek je predao robu'
      },
      {
        znak: '„vozač potvrdio na kraju ture"',
        znaci: 'stanicu je vozač zatvorio odgovorom na kraju ture, a ne na licu '
          + 'mesta — vreme je sa stanice (odlazak, pa dolazak); kad ni toga nema, to '
          + 'je trenutak odgovora, ne predaje'
      },
      {
        znak: '„potvrdio operater"',
        znaci: 'predaju je zabeležio operater posle razgovora, ili odlukom „Ipak '
          + 'isporučeno" na „Sa terena" — vozač to nije štiklirao; vreme je sa '
          + 'stanice ili kraj ture, ne trenutak predaje'
      },
      {
        znak: '„po odlasku sa stanice"',
        znaci: 'vozač je na telefonu zabeležio odlazak sa stanice, ali stavke nije '
          + 'štiklirao — vreme je tog odlaska'
      },
      {
        znak: '„po GPS-u, vozač nije štiklirao"',
        znaci: 'vozilo je bilo tu, ali predaja nije potvrđena'
      },
      { znak: '„bez vremena"', znaci: 'isporuka je zatvorena bez ikakvog vremena' },
      {
        znak: 'belo / žuto vreme',
        znaci: 'belo: vozač je štiklirao na licu mesta; žuto: vreme je uzeto odnekud '
          + 'drugde, a reč uz red kaže odakle'
      },
      { znak: '„potpisano" (zeleno)', znaci: 'kupac se potpisao vozaču — to je dokaz predaje' }
    ],
    zamke: [
      'Do 16.10.2026. je odluka „Ipak isporučeno" sa „Sa terena" stajala ovde '
        + 'bez napomene, kao da je vozač štiklirao na licu mesta. Sad piše '
        + '„potvrdio operater".',
      'GPS vreme nije dokaz predaje. Ako ti treba dokaz, traži potpis ili '
        + 'pozovi vozača — nemoj GPS vreme prepisivati kupcu kao vreme isporuke.'
    ]
  },

  analitika: {
    naslov: 'Analitika',
    cemuSluzi:
      'Na vrhu je brzina — koliko kupac čeka od kupovine do isporuke — i koliko '
      + 'dan vozila traje naspram ture, pa kako kupci ocenjuju vozače. Ispod je '
      + 'koliko ture stvarno traju, '
      + 'mereno po stanici, i koliko to odstupa od procene. Na dnu su pojedinačne '
      + 'ture i „Zašto je menjan redosled" — razlozi koje su vozači prijavili. '
      + 'Ovo je razlog zbog kog se telemetrija beleži od prvog dana: '
      + 'kad se skupi dovoljno merenja, procene se zamenjuju izmerenim.',
    koraci: [
      'Izaberi prozor (30, 90 ili 180 dana) u odeljku „Planirano vs. '
        + 'ostvareno"; isti prozor važi i za brzinu i „Dan vozila" iznad njega. Trideset dana '
        + 'pokazuje sadašnje stanje, sto osamdeset pravu naviku.',
      'Uz svaki broj pogledaj koliko merenja stoji iza njega.',
      'Odstupanje čitaj sa znakom: „+7" je sedam minuta duže od procene, '
        + '„-3" tri minuta kraće.'
    ],
    oznake: [
      {
        znak: '„preko smene"',
        znaci: 'zabeležena tura je trajala duže od 7 sati — isti prag za sve vozače '
          + 'i sve dane, i subotom'
      },
      {
        znak: '„N× redosled"',
        znaci: 'na N stanica vozač je prijavio promenu reda sa razlogom; vožnja '
          + 'drugim redom bez prijave se ovde ne broji'
      },
      { znak: '„N× izuzetak" (crveno)', znaci: 'na turi je vozač N puta prijavio izuzetak' },
      {
        znak: '„N od M (P%)" u „Ocene kupaca"',
        znaci: 'koliko je kupaca ocenilo isporuku od onih kojima je ocena ponuđena '
          + '(svaki potpis kupca); kupac ne mora da oceni'
      },
      { znak: 'siv prosek ocene', znaci: 'vozač ima manje od 10 ocena — još nije slika vozača' },
      { znak: 'crveni broj kod 1★', znaci: 'bilo je ocena sa jednom zvezdicom; komentari su ispod tabele' },
      {
        znak: '„proveri: …" uz vozača',
        znaci: 'ponovo ocenjena stanica, ocena bez potpisa, ili skoro svaka isporuka '
          + 'ocenjena samo peticom — razlog da pozoveš kupca, ne dokaz'
      },
      {
        znak: '„Premalo merenja za zaključke" / „Samo N% stanica ima oba vremena"',
        znaci: 'brojevi ispod postoje, ali im još ne treba verovati — merenja je malo, '
          + 'ili vozači ne beleže i dolazak i odlazak'
      }
    ],
    zamke: [
      'Objekat ili vozač sa manje od tri merenja se ne prikazuje u „Po objektu" '
        + 'i „Po vozaču"; kad nijedan nema tri, ceo odeljak nestane — to ne znači '
        + 'da nije vožen.',
      'Ocenu koju vozač da umesto kupca nije moguće sasvim sprečiti. Zato se '
        + 'zvezdice nude samo u kućici za potpis, broji se samo prva ocena stanice '
        + 'i samo uz potpis, a ponovljena ocena i ocena bez potpisa idu u „proveri".',
      'Medijana od tri merenja i medijana od tri stotine izgledaju isto na '
        + 'ekranu, a ne znače isto. Bez broja merenja bi se po jednom lošem '
        + 'danu menjala procena za ceo pogon.'
    ]
  },

  podesavanja: {
    naslov: 'Podešavanja',
    cemuSluzi:
      'Ono što se namesti jednom i ne dira svaki dan: kompromis brzina–popunjenost, '
      + 'vozači i njihovi linkovi, '
      + 'poruke objektima, GPS uređaji, slanje poruka, mere vozila i zapremine '
      + 'robe, pristup ERP-u. Stoji odvojeno da se ne takmiči za prostor sa onim '
      + 'što gori.',
    koraci: [
      '„Brzina ili popunjenost" — prag popunjenosti i koliko dana roba sme da '
        + 'čeka (posebno za objekte, posebno za kupce). Ovo je kompromis firme: '
        + 'menjaj ga svesno, izmena važi za sve ture od sledećeg osvežavanja.',
      '„Vozači i šifarnik" — usklađivanje vozača sa šifarnikom osoblja u ERP-u '
        + 'i razrešavanje sukoba.',
      '„Linkovi vozača" — odavde se vozaču šalje adresa njegove ture. Link '
        + 'je lični i radi bez prijavljivanja, pa se ne prosleđuje dalje.',
      '„Poruke objektima" — na koji telefon poslovođa dobija Viber šta mu '
        + 'stiže; kad je na godišnjem, ovde se upiše zamenik. Nalozi i podsetnici '
        + 'za prijem idu na mejl objekta iz ERP-a, i dok je zamenik upisan.',
      '„GPS uređaji" — ovde se uređaj sparuje sa vozilom. Bez sparivanja '
        + 'pozicija postoji, ali se ne zna čija je.',
      '„Slanje poruka" — pokazuje da li su Viber, SMS i mejl podešeni; za SMS '
        + 'proverava i nalog i kredit. Odavde ide i probna poruka. Kad servis za poruke ne radi, poruke '
        + 'čekaju u redu i na „Sa terena" se pali traka „Poruke ne idu"; ovde '
        + 'proveri nalog i kredit pre nego što tražiš grešku na terenu.',
      '„Mere vozila i zapremine" — „Upiši ono čega nema" dopisuje dimenzije i '
        + 'zapremine; izmereno se ne dira. Dimenzije zna samo za vozila koja su '
        + 'već izmerena; novo vozilo dobija zapreminu iz registra, ako je tamo '
        + 'upisana. Bez toga popunjenost piše da se ne zna. '
        + '„Preuzmi vozila iz registra" donosi nova vozila, gasi ona koja su u '
        + 'registru ugašena i menja registracije — i ispiše šta je promenio. '
        + 'Pokreni ga kad firma kupi ili proda vozilo; inače novo vozilo ne može '
        + 'na turu.',
      '„Pristup ERP-u" — šta je uključeno (upis, rezervacija, brisanje) i koja '
        + 'prava nalog ima.'
    ],
    oznake: [
      { znak: '„ima link" / „bez linka"', znaci: 'da li vozač već ima link za svoju turu' },
      {
        znak: '„zamenik"',
        znaci: 'poruke objektu trenutno idu zameniku, ne poslovođi — skloni ga kad se poslovođa vrati'
      },
      {
        znak: '„podešen" / „nije podešen" / „nalog radi"',
        znaci: 'kanal za poruke ima pristupne podatke; „nalog radi" se proverava samo za SMS'
      },
      {
        znak: '„uključeno" / „isključeno" (Pristup ERP-u)',
        znaci: 'da li aplikacija sme da radi taj upis u ERP; menja se na serveru, ne ovde'
      }
    ],
    zamke: [
      'Odeljak „Linkovi vozača" se ne pokazuje dok spisak vozača ne stigne. Ako '
        + 'ga nema, osveži — nije nestao.',
      'Prag popunjenosti i dozvoljeno čekanje (za objekte i za kupce) menjaš u '
        + 'odeljku „Brzina ili popunjenost". Server proverava opseg (prag 0–100 %, '
        + 'čekanje 1–30 dana) i beleži ko je promenio. Do 15.10.2026. ih je menjao '
        + 'samo administrator u bazi.',
      'Promena ovde se ne vidi odmah na ostalim ekranima — oni se osvežavaju '
        + 'na svoj ritam. Ako proveravaš da li je nešto leglo, osveži ekran.',
      'Poruke objektu idu poslovođi iz šifarnika kontakata, pa zameniku '
        + 'poslovođe; ostali vezani za objekat (npr. podrška) dobijaju ih samo kad '
        + 'objekat nema nijednog od njih dvojice. Izmereno 19.10.2026: za Kulu je '
        + 'prva u šifarniku bila podrška, pa je za 60 dana 159 poruka za Kulu otišlo '
        + 'njoj, a na mejl Kule nijedna.'
    ]
  },

  objekat: {
    naslov: 'Ekran poslovođe',
    cemuSluzi:
      'Tri kartice, po pitanju koje postavljaju. „Šta me čeka" je sve što traži '
      + 'tvoju ruku i ništa drugo. „Moj razvoz" je roba koju tvoj objekat vozi '
      + 'sam, svojim vozilom. „Pregled" je šta se dešava sa tvojom prodajom — '
      + 'tamo se ne radi ništa.',
    koraci: [
      'Redosled odeljaka nije slučajan: prvo ono što DRŽI ROBU, pa ono što '
        + 'DRŽI KNJIGU.',
      '„Rezervacije za kupce" stoje na vrhu kad imaju alarm: kupac je kupio robu '
        + 'koju je magacin odvojio za tvoj objekat (nalog „ZA <objekat> REZERVACIJA" '
        + 'u magacinu 008). Proveri da li je to taj kupac i pritisni „Jeste taj kupac" '
        + 'ili „Nije taj kupac". Robu sa naloga skida magacin; isti alarm stiže i '
        + 'Viberom, sa linkom pravo na ovaj odeljak.',
      '„Roba za kupce" — kupac dolazi sam (9999001/9999002) ili ga voziš ti '
        + '(9999013), a roba je bila drugde: dovozi je logistika i ovde piše dokle je '
        + 'stigla. Kad stigne, dobijaš Viber i red piše „stiglo" — javi kupcu. Kad je '
        + 'kupac odnese, klikni „Kupac preuzeo" i potvrdi: otpremnica se odmah sama '
        + 'kukiči u ERP-u. Robu koju voziš ti ne preuzima kupac ovde — ona posle '
        + 'dolaska ide u „Moj razvoz".',
      '„Adrese za ispravku" dolaze odmah zatim, jer bez njih roba ne može na turu, a '
        + 'ti si najbolji da ih ispraviš — kupac je tvoj (operater vidi iste '
        + 'adrese u svom redu). Postavi tačku dok ti je kupac na vezi, tada '
        + 'saznaješ i sprat i lift.',
      '„Odakle roba ide" — potvrdi odakle se diže; pre upisa se pita. Potvrda '
        + 'odmah pravi prenos u 012 sa izabranog mesta, pa taj komad niko drugi ne '
        + 'može da proda. Pogrešno mesto se menja izborom drugog i novom potvrdom, '
        + 'dok isporuka nije na turi: stari nalog se briše ako nije proknjižen (do '
        + '16.10.2026. posle potvrde nije bilo drugih mesta). '
        + 'Ako je izabrano mesto tvoj objekat, prenos ne treba — roba je već kod '
        + 'tebe, i piše „nije potreban prenos". '
        + 'Magacin sme da složi turu i pre tvoje potvrde. Ako uz otpremnicu piše '
        + '„Ranija odluka pokazuje na mesto gde robe više nema", roba je sa tog '
        + 'magacina u međuvremenu otišla — proveri i potvrdi ponovo (istu, ako je '
        + 'vidiš na polici; drugu, ako je nema). Kad roba koju si upisao kao '
        + 'naručenu („naručeno je, stiže" ili „ne diži — naručeno od dobavljača") '
        + 'stigne, stavka se vraća u listu sa tim mestom već '
        + 'ponuđenim — potvrdi je jednim klikom; do tada ne piše „potvrđeno". '
        + 'Ako je kupac svoju '
        + 'robu već uzeo u radnji, NE biraj mesto — klikni „Kupac preuzeo ovde": '
        + 'isporuka izlazi iz prevoza, a otpremnicu posle kukiči u ERP-u.',
      '„E-fakture — vozimo li?" — reci da li tu robu mi vozimo ili kupac dolazi '
        + 'sam. Pogrešan odgovor pravi turu za robu koju niko ne čeka, ili '
        + 'ostavlja kupca da čeka vozilo koje ne ide.',
      '„Odvoji za tuđeg kupca" — roba koju je prodao drugi objekat, a stoji kod '
        + 'tebe kao slobodna. Prenesi je u 012 da je ne prodaš drugom. Red dobija '
        + 'zelenu oznaku „preneseno" tek kad ERP pokaže TVOJ prenos u 012 — tuđi '
        + 'prenos istog artikla se ne računa, a jedan nalog pokriva onoliko redova '
        + 'koliko ima komada. Ako je '
        + 'kupac robu odneo sam, klikni „Kupac odneo sam" i potvrdi — to skida '
        + 'robu sa prevoza i upisuje prenos u ERP, pa se pita pre upisa.',
      '„Pošalji u magacin" — roba koja stoji kod tebe (trgovac je upisao tvoj '
        + 'objekat kao mesto otpreme), a kupca danas ne pokriva nijedan pravac. '
        + 'Napravi interni prenos u magacin, kao i svaki drugi; tura je posle nosi. '
        + 'Ako je poslovođa objekta koji je prodao izabrao drugo mesto, molba ide '
        + 'tamo, ne tebi.',
      '„Nije označeno kao isporučeno" i „Nalozi za knjiženje" — to drži knjigu. '
        + 'Radi ih posle onoga gore, ali ih ne ostavljaj da stoje.',
      'Brojka na sklopljenom odeljku nosi boju. Ako je obojena, unutra te čeka '
        + 'posao — ne moraš da otvaraš sve.',
      '„Moj razvoz" — označi isporuke redom kojim ćeš obilaziti, izaberi ko vozi '
        + 'i vozilo, pa „Složi turu i pokaži QR". Radnik skenira QR telefonom kojim '
        + 'vozi; QR stoji uz turu dok se ne završi.'
    ],
    oznake: [
      { znak: 'brojka na kartici „Šta me čeka"', znaci: 'koliko poslova čeka tebe, zbir svih odeljaka' },
      { znak: '„danas" / „N d"', znaci: 'koliko dana roba čeka; žuto posle 2 dana' },
      {
        znak: '„Prodata rezervisana roba" (Rezervacije za kupce)',
        znaci: 'kupac je kupio robu sa naloga rezervacije tvog objekta — proveri da li je '
          + 'to taj kupac; alarm se zatvori sam kad magacin skine robu sa naloga'
      },
      { znak: '„preneseno" (zeleno)', znaci: 'ERP pokazuje tvoj prenos u 012 — posao je gotov' },
      {
        znak: '„čeka prevoz" / „na turi" / „stiglo" (zeleno) / „preuzeto" (Roba za kupce)',
        znaci: 'dokle je roba za kupca stigla; „stiglo" znači da je kupac čeka u objektu — '
          + 'javi mu; „preuzeto" čeka još samo oznaku u ERP-u'
      },
      {
        znak: '„(već u objektu)" uz stavku (Roba za kupce)',
        znaci: 'ta roba nije putovala — bila je kod tebe od početka; kupac je uzima uz ostalo'
      },
      {
        znak: '„prenos u 012 nije prošao" i „Potvrdi ponovo"',
        znaci: 'potvrda je zabeležena, ali komad nije odvojen — drugi objekat ga može '
          + 'prodati. Potvrdi ponovo; ako opet ne prođe, javi operateru. Do 16.10.2026. '
          + 'je posle osvežavanja ovde pisalo „potvrđeno"'
      },
      {
        znak: '„Možda je prenos otišao u …"',
        znaci: 'tvoj prenos istog artikla je otišao u drugi magacin, ne u 012 — proveri nalog'
      },
      { znak: '„mi smo isporučili" (crveno)', znaci: 'tura je predala robu, a u ERP-u nije kukičano' },
      { znak: '„stoji N d" (žuto)', znaci: 'otpremnica stoji 14 i više dana, a kod nas nema traga da je vožena' },
      {
        znak: '„nema ga u ERP-u" (nalozi za knjiženje)',
        znaci: 'aplikacija je nalog upisala, a ERP ga ne pokazuje — pozovi magacin pre knjiženja'
      },
      { znak: 'crveni dani uz nalog', znaci: 'nalog čeka knjiženje duže od jednog dana' },
      {
        znak: 'red stanja iznad otpremnice',
        znaci: '„čeka da izabereš…", „potvrđeno — čeka magacin…", „na turi …", ili '
          + 'zeleno „predato kupcu …"'
      },
      {
        znak: '„— upisao trgovac" / „— prodati komad je tu" / „— N kom" / „— roba je odavde već krenula"',
        znaci: 'šta se zna o robi na tom mestu u listi za izbor'
      },
      { znak: '„(kasni)" uz naručeno', znaci: 'obećani datum je prošao, a roba nije stigla' },
      { znak: '„poslednji komad na nekom mestu"', znaci: 'negde ima tačno koliko treba — često izložbeni komad' },
      {
        znak: '„obećanje ne važi — roba je već krenula iz X"',
        znaci: 'roba za tu otpremnicu je već na putu odande; poništi obećanje'
      },
      { znak: '„N dana" crveno (Pregled)', znaci: 'isporuka čeka duže od dozvoljenog broja dana' },
      {
        znak: 'stanje uz red „Na turi" (Pregled)',
        znaci: '„u vozilu — tura je na putu", „na turi … za dd.MM., još nije krenula", '
          + '„predato … — čeka knjiženje", „vozač javio: … — čeka odluku operatera". '
          + 'Do 16.10.2026. je za sve pisalo „u vozilu", i za turu zakazanu za sutra'
      },
      { znak: '„potpisano" (Pregled)', znaci: 'kupac se potpisao vozaču' },
      {
        znak: '„Kupac preuzeo ili odneo" i „Povuci izjavu" (Pregled)',
        znaci: 'tvoje izjave da je kupac robu uzeo sam. Pogrešan klik se povlači '
          + 'dugmetom — isporuka se vraća u prevoz. Kad uz izjavu piše upisan nalog, '
          + 'povlačenje ide preko operatera, jer taj nalog treba poništiti u ERP-u'
      },
      {
        znak: '„Sklonjeno iz prevoza" (Pregled)',
        znaci: 'otpremnice tvoje prodaje koje je operater sklonio („Ne vozi se"), sa '
          + 'razlogom. Do 16.10.2026. su nestajale bez traga'
      },
      {
        znak: 'polje „Traži kupca, adresu ili otpremnicu" (Pregled)',
        znaci: 'kad kupac zove: ukucaj prezime ili broj otpremnice, pa vidiš da li čeka, '
          + 'na kojoj je turi ili kad je predato'
      },
      {
        znak: '„nije krenula" / „na putu" / „gotova" / „ostala otvorena" (Moj razvoz)',
        znaci: 'stanje tvoje ture; otvorena tura drži robu dok je radnik ne završi'
      }
    ],
    zamke: [
      'Kad ERP ne radi, ostaješ prijavljen: na vrhu piše „ERP trenutno ne '
        + 'odgovara", i usred rada, a delovi koji čitaju ERP zadržavaju ono što je '
        + 'poslednji put stiglo. Dok neki deo ne stiže, ne piše „Sve je čisto" nego '
        + 'šta se ne učitava. Ne prijavljuj se ponovo — sačekaj ili osveži; traka '
        + 'nestaje najkasnije minut pošto se ERP vrati. Kad na vrhu piše da baza '
        + 'aplikacije nije ažurirana, javi šefu magacina — to nije pad ERP-a. Kad '
        + 'piše da prijava više ne važi, osveži stranicu i prijavi se. Do '
        + '16.10.2026. je pad ERP-a usred rada praznio spiskove i pisao da posla nema.',
      'Kad roba stigne, prijem u ERP-u radiš ti. Dok to ne uradiš, aplikacija '
        + 'misli da roba još čeka prevoz, pa ista roba može da uđe u drugu turu. '
        + 'Operater je vidi kao neproknjižen prijem, a tebi stiže podsetnik ujutru '
        + 'i popodne.',
      'Dva prijema na isti prenos ostavljaju u tvojoj prodavnici komad koji ne '
        + 'postoji, a minus se posle sam prekrije pa ga niko ne vidi. Ako nisi '
        + 'siguran da li si proknjižio, proveri — nemoj knjižiti „za svaki '
        + 'slučaj".',
      'Belo vreme isporuke znači da je vozač štiklirao predaju. Žuto znači da '
        + 'je vreme uzeto odnekud drugde, a sa oznakom „(GPS)" samo da je vozilo '
        + 'bilo tu, ne da je roba predata.',
      'Otpremnica na kojoj je deo robe predat, a deo još čeka (po dogovoru sa '
        + 'kupcem ili jer nije stigao), ne stoji na „Nije označeno kao '
        + 'isporučeno". Kukiči je tek kad stigne sve — inače ostatak nestaje iz '
        + 'logistike i kupac ga ne dobije. Do 14.10.2026. je ekran za takvu '
        + 'otpremnicu pogrešno tražio kukičanje.',
      '„Moj razvoz" ne nosi ono što je logistika već predala kupcu, ni ono što '
        + 'po dogovoru sa kupcem ostaje za kasnije — uz red piše šta je '
        + 'izostavljeno. Do 15.10.2026. je prebačena otpremnica stajala cela, pa '
        + 'bi prodavnica vozila i aparat koji kupac već ima. Isto važi za '
        + '„E-fakture": „ne vozimo" ne prolazi dok je faktura na živoj turi — '
        + 'prvo je magacin skida sa ture.',
      'Kad trgovac upiše mesto otpreme, aplikacija za svežu otpremnicu u roku '
        + 'od desetak minuta pravi prenos u 012, ako tamo ima robe. Ako na tom '
        + 'mestu posle više nema dovoljno, u listi stoji sa „roba je odavde već '
        + 'krenula"; ako ima, stoji sa brojem komada. U oba slučaja izaberi njega, '
        + 'ne „naručeno je, stiže". 13.10.2026. je '
        + 'za Alfa plam koji je čekao kamion u SO2 upisano da se naručuje, i '
        + 'isporuka je stala.',
      'Tvoj izbor mesta je jači od trgovčevog upisa: kad izabereš drugo mesto, '
        + 'aplikacija prenos pravi odande i ne vraća ga na mesto iz računa. Isti '
        + 'artikal u dva reda ide jednim prenosom za sve komade; ako su redovi '
        + 'upisani sa različitih mesta, aplikacija ne pogađa — čeka tvoj izbor. '
        + 'Do 14.10.2026. je u tom slučaju odvajala samo prvi komad.',
      '„Roba ide iz SO1" i „kupac preuzeo u SO1" nisu isto. Prvo kaže magacinu '
        + 'da vozi robu kupcu na adresu; drugo je skida sa prevoza. 14.10.2026. '
        + 'su dve otpremnice SO1 koje su kupci već odneli stajale operateru za '
        + 'razvoz, jer je za njih potvrđeno „ide iz SO1". Najbolje je da trgovac '
        + 'već pri prodaji izabere „kupac nosi sam" (252).',
      '„Kupac preuzeo ovde" odbija ako je stavka već na turi (javi operateru) '
        + 'ili ako je roba već odvojena iz drugog mesta — tada taj prenos treba '
        + 'vratiti, pa ne upisuje ništa i kaže zašto.',
      'Kupac koji dolazi sam a roba je drugde: od 21.10.2026. NE praviš klasičan '
        + 'prenos kroz 002 — robu dovozi logistika, a prenos u 012 aplikacija pravi '
        + 'sama. Klasičan prenos bi zadužio magacin dvaput. 19.10.2026. je račun '
        + '004-1-5500 (klima iz 021 za kupca u Kuli) posle ispravke napomene na '
        + '9999002 nestao iz razvoza, a prevoz se nigde nije pojavio — zato sad stoji '
        + 'u „Roba za kupce". Za takvu robu u „Odakle roba ide" nema „Kupac preuzeo '
        + 'ovde": preuzimanje se beleži kad roba stigne.',
      '„Moj razvoz" ne nudi robu koja tek stiže turom logistike — uz red piše '
        + '„Roba još nije u objektu — logistika je dovozi …". Do 21.10.2026. je '
        + 'podrazumevao da je roba već u prodavnici.'
    ]
  },

  vozac: {
    naslov: 'Ekran vozača',
    cemuSluzi:
      'Tvoja tura — današnja, ili ranija koja nije zatvorena (tada piše „od '
      + '<datum>"). Radi i bez mreže — štikliranje, potpis, beleške i fotografije se čuvaju '
      + 'na telefonu i pošalju sami kad se signal vrati.',
    koraci: [
      'Pre polaska otvori turu dok imaš signal. Tada se učita cela, pa posle '
        + 'radi i bez mreže.',
      'Kad krećeš, pritisni „Krenuo sam". Od tada se meri tura i vidi se „X od '
        + 'Y stanica".',
      'Na stanici prvo pritisni „Stigao sam", pa štikliraj šta si predao — ili '
        + '„Predao sam sve" jednim potezom. Potpis kupca je naš dokaz o isporuci; '
        + 'gde je obavezan, stanica se bez njega ne zatvara. Jedini izuzetak je '
        + 'stanica koju zatvaraš tek na kraju ture — tada kupca više nema, pa se '
        + 'potpis ne traži; zato potpis uzmi na stanici.',
      'Kad kupac povuče potpis, u praznom polju ispod potpisa pojave se zvezdice '
        + 'za ocenu dostave. '
        + 'Telefon neka drži kupac i neka sam odluči — ocena nije obavezna, a '
        + 'komentar može samo uz jednu zvezdicu. Ne ocenjuj umesto kupca: '
        + 'ponovljena ocena se ne broji, a operater je vidi.',
      'Sa stanice odlaziš sa „Odlazim sa stanice". Ako nešto nije predato, pita '
        + 'se šta sa tim; ako nema potpisa, traži se potpis ili razlog.',
      'Ako si štiklirao nešto što nisi predao, tapni štikliranu kućicu i '
        + 'potvrdi „Da, vrati". To možeš dok sam ne zatvoriš turu; posle toga '
        + 'javi šefu magacina.',
      'Ako nešto ne ide po planu, prijavi izuzetak jednim tapom. Razlog biraj '
        + 'tačan — po tim razlozima se vidi šta se na terenu dešava.',
      'Kad je roba oštećena, kupac je odbio ili si robu ostavio negde gde ćeš '
        + 'morati da objasniš — „Fotografija" na stanici. Napomena uz sliku nije '
        + 'obavezna, ni sama slika. Radi i bez mreže.',
      'Redosled stanica je predlog, ne naredba. Ti znaš teren; kad promeniš '
        + 'redosled, aplikacija traži razlog jednim tapom.',
      'Ako vozilo stane, pritisni „Vozilo stalo" — šef to odmah vidi.',
      'Na kraju pritisni „Završi turu". Za stanice bez odgovora reci šta je bilo; '
        + 'robu koja ostaje predaj u magacinu.',
      'Dok nešto nije poslato, na vrhu stoji „N zapisa još nije poslato". Kad '
        + 'traka nestane, sve je otišlo. Aplikaciju smeš da zatvoriš — zapisi '
        + 'ostaju u telefonu — ali ne briši podatke pregledača i ne menjaj telefon.'
    ],
    oznake: [
      { znak: 'UZIMA SE', znaci: 'na ovoj stanici robu PODIŽEŠ, ne ostavljaš' },
      {
        znak: '„za kupca" uz stavku',
        znaci: 'roba za kupca koji dolazi u taj objekat — predaj je odvojeno od dopune, '
          + 'da je ne stave na policu'
      },
      { znak: 'DODATO', znaci: 'šef je stanicu dodao posle polaska — nije je bilo kad si krenuo' },
      { znak: 'OTKAZANO', znaci: 'stanica je skinuta sa ture; ne ide se tamo, roba se ne ostavlja' },
      {
        znak: '„BEZ BROJA (bb)"',
        znaci: 'kuća nema broj; tačka je na ulici, ne pred kućom — pozovi kupca'
      },
      {
        znak: '„dva čoveka"',
        znaci: 'isporuka traži dvojicu — od 2. sprata bez lifta, ili je tako označeno '
          + 'pri potvrdi adrese'
      },
      {
        znak: '„KUPAC NIJE OBAVEŠTEN"',
        znaci: 'poruka da roba stiže nije prošla, ili čeka u redu duže od 15 minuta (kvar '
          + 'kanala), ili je broj ispravljen posle najave pa novi broj poruku nije dobio — '
          + 'kupac te ne očekuje; pozovi ga pre dolaska. Oznaka i telefoni kupaca se '
          + 'osvežavaju usput, posle svakog slanja'
      },
      {
        znak: '„N zapisa još nije poslato"',
        znaci: 'koliko tvojih zapisa još nije stiglo na server; „Pošalji sad" pokušava odmah'
      },
      { znak: '„mreža" / „bez mreže"', znaci: 'ima li telefon sada signal; bez mreže se radi normalno' },
      { znak: '„N/M isporučeno" / „utovareno"', znaci: 'koliko je stavki sa stanice štiklirano' },
      { znak: '✓ uz stanicu', znaci: 'otišao si sa stanice' },
      { znak: '„Tura je izmenjena: …"', znaci: 'šef je promenio turu dok si bio na putu' },
      {
        znak: '„Na ovom telefonu stoji tura drugog vozača…"',
        znaci: 'tuđi neposlati zapisi; šalju se sami — ne briši podatke'
      },
      { znak: '„Potpis je obavezan"', znaci: 'stanica se ne zatvara bez potpisa; nema ponuđenih razloga' },
      { znak: '„Javljeno da je vozilo stalo u HH:MM"', znaci: 'kvar je zapisan i šef ga vidi' },
      {
        znak: '★★★★★ u kućici za potpis',
        znaci: 'kupac ocenjuje prevoz i isporuku; ocena se vodi uz tvoje ime i vidi je uprava'
      },
      {
        znak: '„server nije primio"',
        znaci: 'server je zapis odbio i ponovno slanje ne pomaže — javi šefu '
          + 'magacina šta piše, pa klikni „Javio sam — skloni"'
      },
      { znak: '„Potpis prikupljen ✓"', znaci: 'potpis je sačuvan na telefonu' },
      {
        znak: '„N fotografija čeka slanje"',
        znaci: 'slike su na telefonu i otići će same kad se signal vrati; do '
          + 'tada ih šef ne vidi'
      }
    ],
    zamke: [
      'Brojač „X od Y stanica" broji samo stanice koje se voze: skinuta ostaje '
        + 'precrtana na spisku, ali ne ulazi u brojač. Ako je telefon pun, potpis '
        + 'se ne gubi ćutke — kućica za potpis ostaje otvorena i u njoj piše da '
        + 'potpis nije sačuvan (uhvati signal da se red isprazni, pa potpiši '
        + 'ponovo). Do 16.10.2026. je ta poruka stajala ispod kućice, nevidljiva.',
      'Nov link usred ture (šef ga napravi iznova) stari link gasi. Otvori nov '
        + 'link čim bude signala: tura se učita, a sve što si uradio bez signala '
        + 'sa starim linkom pošalje se novim — ništa se ne gubi. Bez signala nov '
        + 'link turu ne pokazuje, ali kaže da postoji. Do 16.10.2026. je taj rad '
        + 'ostajao zauvek u telefonu. Tuđi zapisi na deljenom telefonu ostaju '
        + 'tom vozaču.',
      'Ako na vrhu piše da server ne prima zapise, zapisi su sačuvani u telefonu '
        + 'i šalju se ponovo — javi šefu magacina šta piše. Do 16.10.2026. se to '
        + 'nije videlo: rastao je samo broj neposlatog.',
      'Ako se pojavi „Sat telefona kasni/žuri", podesi sat (automatsko vreme). '
        + 'Vremena koja pošalješ server ispravlja za izmereno odstupanje, a '
        + 'nemoguće vreme (npr. iz 2020. posle restarta bez mreže) zamenjuje '
        + 'vremenom slanja i to zabeleži uz zapis — ali tačan sat daje tačno '
        + 'vreme predaje, ispravljeno je samo približno. Do 15.10.2026. je '
        + 'pogrešno vreme prolazilo bez reči i nije se moglo ispraviti.',
      'Posle završene ture „Završeno — uzmi sledeću turu" otvara sledeću '
        + 'nezavršenu, i kad si prvu otvorio linkom iz poruke. Do 16.10.2026. je '
        + 'tada ponovo otvaralo istu, završenu turu.',
      'Zvezdice se nude samo pri PRVOM potpisu kupčeve stanice. Kad potpis '
        + 'ponoviš, zvezdica nema — to je namerno, da se ocena ne bi popravljala.',
      'Telefon ne treba okretati da bi se videlo „Sačuvaj potpis". Do '
        + '17.10.2026. su zvezdice gurale dugmad ispod ivice ekrana i pomagalo je '
        + 'tek okretanje; sada je mesto za njih rezervisano od početka.',
      'Ne biraj približan razlog. „Aparat oštećen" i „Kupac odbio robu" posle '
        + 'izgledaju kao dva različita problema — jedan je naša roba, drugi je '
        + 'kupac.',
      'Vreme isporuke se beleži po satu tvog telefona, ne po trenutku '
        + 'sinhronizacije. Zato štrikliraj kad predaš, ne kad uhvatiš signal.',
      'Fotografije stoje u pregledaču telefona dok ne odu. Ako obrišeš podatke '
        + 'pregledača dok na vrhu stoji da nešto nije poslato, slike su izgubljene.'
    ]
  }
};
